/**
 * CompleteGoogleProfile — "One Last Step" phone collection for Google sign-in users.
 *
 * Single-step flow (no OTP): enter phone → save → /home.
 * "Use email instead" lets the user skip and set it later via Profile.
 *
 * Guards:
 *  - Not logged in       → /auth
 *  - Already has phone   → /home  (handles back-button / direct nav)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getPostLoginRedirect } from '../../utils/storage';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { countryCodes } from '../../utils/countryCodes';

const API = process.env.REACT_APP_BACKEND_URL;

const UNIQUE_CODES = Array.from(
  new Map(countryCodes.map((c) => [c.code, c])).values(),
);

export default function CompleteGoogleProfile() {
  const navigate = useNavigate();
  const { user, loading, updateUser } = useAuth();

  const [busy, setBusy]               = useState(false);
  const [countryCode, setCountryCode] = useState('+673');
  const [localNumber, setLocalNumber] = useState('');

  // Guards
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/auth', { replace: true }); return; }
    if (user.phone) { navigate(getPostLoginRedirect(), { replace: true }); }
  }, [user, loading, navigate]);

  const handleSave = async () => {
    const cleaned = localNumber.replace(/\D/g, '');
    if (cleaned.length < 6) { toast.error('Please enter a valid phone number'); return; }
    const fullPhone = `${countryCode}${cleaned}`;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/me/set-phone`,
        { phone: fullPhone },
        { withCredentials: true },
      );
      updateUser(data);
      toast.success('Phone number saved!');
      navigate(getPostLoginRedirect(), { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save phone number. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = () => {
    navigate(getPostLoginRedirect(), { replace: true });
  };

  if (loading || !user) return null;

  // First initial for avatar fallback
  const initial = (user.name || user.email || '?')[0].toUpperCase();

  return (
    <div
      data-testid="complete-google-profile-page"
      className="mobile-container min-h-screen flex flex-col"
      style={{ background: '#F5F7FA' }}
    >
      {/* ── BBA hero header ── */}
      <div
        className="w-full flex flex-col items-center justify-center py-8 px-4"
        style={{
          background: 'linear-gradient(160deg, #0a1628 0%, #1a2a4a 60%, #0d1f3c 100%)',
          minHeight: 160,
        }}
      >
        <img
          src="/images/bba-logo.jpg"
          alt="BBA Logo"
          className="w-16 h-16 rounded-full object-cover mb-2"
        />
        <p
          className="text-xs font-bold tracking-widest uppercase"
          style={{ color: '#ffffff', letterSpacing: '0.15em' }}
        >
          Brunei Basketball Association
        </p>
      </div>

      {/* ── Card ── */}
      <div className="flex-1 flex flex-col items-center px-4 -mt-6">
        <div
          className="w-full max-w-sm rounded-3xl shadow-lg p-7"
          style={{ background: '#ffffff' }}
        >
          {/* Avatar */}
          <div className="flex justify-center mb-4">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-16 h-16 rounded-full object-cover ring-2"
                style={{ ringColor: '#FF6B00' }}
                data-testid="complete-profile-avatar-photo"
              />
            ) : (
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-black text-white"
                style={{ background: '#FF6B00' }}
                data-testid="complete-profile-avatar-initial"
              >
                {initial}
              </div>
            )}
          </div>

          {/* Heading */}
          <h1
            className="text-xl font-black text-center uppercase tracking-wide mb-1"
            data-testid="complete-profile-heading"
            style={{ color: '#1a1a1a', letterSpacing: '0.05em' }}
          >
            One Last Step
          </h1>
          <p className="text-sm text-center mb-6" style={{ color: '#555' }}>
            Hi <strong>{user.name}</strong>! We need your phone number for booking management.
          </p>

          {/* Phone label */}
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#888' }}>
            Phone Number
          </p>

          {/* Country code + number */}
          <div className="flex gap-2 mb-5">
            <Select value={countryCode} onValueChange={setCountryCode}>
              <SelectTrigger
                className="h-12 w-[110px] rounded-xl"
                data-testid="complete-profile-country-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {UNIQUE_CODES.map((c) => (
                  <SelectItem key={c.code + c.country} value={c.code}>
                    <span className="flex items-center gap-2">
                      <span>{c.flag}</span>
                      <span>{c.code}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <Phone
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: '#aaa' }}
              />
              <Input
                type="tel"
                inputMode="numeric"
                placeholder="Phone number"
                value={localNumber}
                onChange={(e) => setLocalNumber(e.target.value.replace(/\D/g, ''))}
                className="h-12 rounded-xl pl-9"
                data-testid="complete-profile-phone-input"
                disabled={busy}
                maxLength={12}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              />
            </div>
          </div>

          {/* Primary CTA */}
          <Button
            type="button"
            onClick={handleSave}
            disabled={busy || localNumber.replace(/\D/g, '').length < 6}
            className="btn-primary w-full h-14 text-base font-bold uppercase tracking-wide"
            data-testid="complete-profile-save-btn"
          >
            {busy ? 'Saving…' : 'Complete Sign-Up'}
          </Button>

          {/* Skip */}
          <div className="text-center mt-5">
            <button
              type="button"
              onClick={handleSkip}
              className="text-sm font-medium transition-opacity opacity-60 hover:opacity-100"
              style={{ color: '#555' }}
              data-testid="complete-profile-skip-btn"
            >
              Use email instead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
